/**
 * PartnerCashControl - the partner's view of Lusuku cash their riders are
 * physically carrying, and the limit past which a rider stops being offered
 * work.
 *
 * WHY THIS SCREEN EXISTS
 * The blocking is silent by design on the dispatch side: a rider over their
 * limit simply stops being considered for orders. From the partner's desk that
 * looks like a rider who "went quiet" for no reason, and the usual guess is a
 * bug in the app. So the one thing this screen must do well is name the cause:
 * who is blocked, how much cash is behind it, and what the limit is that
 * produced it.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog";
import { getUserFromLocalStorage } from "../../utilities/user";
import {
  apiFetchPartnerCod,
  apiUpdatePartnerCodPolicy,
  apiUpdateRiderCodThreshold,
  apiUpdateRiderVehicle,
} from "../../api/index.js";
import {
  colors,
  font,
  text,
  card,
  cardBody,
  grid,
  inp,
  button,
  pill,
  ugx,
} from "./partnerTheme";
import PartnerHelp from "./partnerHelp";
import AppSelect from "../../components/ui/AppSelect";

/* The dispatcher matches orders to carrier class by these exact strings, so
   they are not a display list to be prettified. */
const VEHICLE_TYPES = ["Bicycle", "Motorcycle", "Car", "Van", "Truck", "Walking"];

/* Every 3PL endpoint answers failures in the same envelope. Surfacing the
   server sentence matters here because the useful refusals are specific
   ("threshold above the platform maximum"), and a generic "something went
   wrong" sends the partner to support instead of to the field they typed in. */
const serverMessage = (err, fallback) =>
  err?.response?.data?.error?.message || fallback;

/* Utilisation is the only place on the screen where colour carries the whole
   message, so the thresholds live in one function rather than inline. */
const utilisationTone = (u) => {
  if (u >= 1) return colors.red;
  if (u >= 0.6) return colors.amber;
  return colors.green;
};

const StatCard = ({ label, value, sub, tone, dominant }) => (
  <div
    style={{
      ...card,
      marginBottom: 0,
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    <span style={text.label}>{label}</span>
    <span
      style={{
        fontFamily: font.mono,
        fontVariantNumeric: "tabular-nums",
        fontSize: dominant ? 26 : 20,
        fontWeight: 700,
        color: tone || colors.ink,
        lineHeight: 1.15,
        wordBreak: "break-word",
      }}
    >
      {value}
    </span>
    {sub ? <span style={{ ...text.help, margin: 0 }}>{sub}</span> : null}
  </div>
);

const UtilisationBar = ({ value }) => {
  const u = Math.max(0, Math.min(1, Number(value || 0)));
  return (
    <div style={{ minWidth: 110 }}>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: colors.lineSoft,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${u * 100}%`,
            height: "100%",
            background: utilisationTone(u),
            borderRadius: 999,
            transition: "width 0.2s",
          }}
        />
      </div>
      <span style={{ ...text.help, margin: "4px 0 0", display: "block" }}>
        {Math.round(u * 100)}% of limit
      </span>
    </div>
  );
};

const PartnerCashControl = () => {
  const history = useHistory();
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const operationMode = user?.result?.operationMode;
  const isPartnerAccount = operationMode === "3PL_PARTNER";
  const permissions = user?.result?.permissions || {};

  /* The server re-checks both of these on every write, and answers 403 when
     they do not hold. Gating them in the UI is therefore not the security
     boundary; it only keeps the screen from offering a button whose single
     outcome would be a refusal the operator cannot act on. The owner always
     passes, staff pass on the matching permission.
     The fleet limit moves every rider at once, so it is financial, not
     operational, and rides on `financials`. */
  const canSetFleetLimit = isPartnerAccount || permissions?.financials === "full";
  const canEditRiders = isPartnerAccount || permissions?.riders === "full";

  const confirmDialog = useConfirmDialog();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [policy, setPolicy] = useState(null);
  const [totals, setTotals] = useState(null);
  const [riders, setRiders] = useState([]);

  const [fleetLimit, setFleetLimit] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);

  /* One editor open at a time: { riderId, mode: "limit" | "vehicle" }. */
  const [editor, setEditor] = useState(null);
  const [limitDraft, setLimitDraft] = useState("");
  const [vehicleDraft, setVehicleDraft] = useState({
    carrierType: "",
    plateNumber: "",
    model: "",
    color: "",
    capacity: "",
  });
  const [savingRow, setSavingRow] = useState(false);

  const applyPayload = useCallback((payload) => {
    if (!payload) return;
    if (payload.policy) {
      setPolicy(payload.policy);
      setFleetLimit(String(payload.policy.defaultRiderThreshold ?? ""));
    }
    if (payload.totals) setTotals(payload.totals);
    if (Array.isArray(payload.riders)) setRiders(payload.riders);
  }, []);

  const load = useCallback(async () => {
    if (!partnerId) {
      setLoading(false);
      setError("No partner account is attached to this login, so there is no cash board to show.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await apiFetchPartnerCod(partnerId);
      if (res?.data?.success) {
        applyPayload(res.data.data);
      } else {
        setError("The cash board could not be loaded.");
      }
    } catch (err) {
      setError(serverMessage(err, "The cash board could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [partnerId, applyPayload]);

  useEffect(() => {
    load();
  }, [load]);

  const min = Number(policy?.min ?? 0);
  const max = Number(policy?.max ?? 0);

  const saveFleetLimit = () => {
    const next = Number(fleetLimit);
    if (!Number.isFinite(next)) {
      toast.error("Enter the limit as a number.");
      return;
    }
    if (next < min || next > max) {
      toast.error(`The fleet limit has to sit between ${ugx(min)} and ${ugx(max)}.`);
      return;
    }
    /* Confirmed rather than fired on click: this one save can block or unblock
       riders who are mid-shift, and the count only comes back afterwards. */
    confirmDialog.ask({
      title: "Change the fleet limit?",
      body: `Every rider still on the default moves to ${ugx(next)}. Riders already holding more than that stop being offered orders until they deposit.`,
      tone: "warning",
      confirmLabel: "Change limit",
      busyLabel: "Saving",
      onConfirm: async () => {
        try {
          setSavingPolicy(true);
          const res = await apiUpdatePartnerCodPolicy(partnerId, next);
          if (res?.data?.success) {
            applyPayload(res.data.data);
            toast.success(res.data.message || "Fleet limit updated.");
          } else {
            toast.error("The fleet limit was not saved.");
          }
        } catch (err) {
          toast.error(serverMessage(err, "The fleet limit was not saved."));
        } finally {
          setSavingPolicy(false);
        }
      },
    });
  };

  const openLimitEditor = (rider) => {
    setEditor({ riderId: rider._id, mode: "limit" });
    setLimitDraft(String(rider.threshold ?? policy?.defaultRiderThreshold ?? ""));
  };

  const openVehicleEditor = (rider) => {
    setEditor({ riderId: rider._id, mode: "vehicle" });
    setVehicleDraft({
      carrierType: rider.carrierType || "",
      plateNumber: rider.plateNumber || "",
      model: "",
      color: "",
      capacity: "",
    });
  };

  const closeEditor = () => {
    if (savingRow) return;
    setEditor(null);
  };

  const saveRiderLimit = async (rider) => {
    const next = Number(limitDraft);
    if (!Number.isFinite(next)) {
      toast.error("Enter the limit as a number.");
      return;
    }
    if (next < min || next > max) {
      toast.error(`This rider's limit has to sit between ${ugx(min)} and ${ugx(max)}.`);
      return;
    }
    try {
      setSavingRow(true);
      const res = await apiUpdateRiderCodThreshold(partnerId, rider._id, next);
      if (res?.data?.success) {
        const d = res.data.data || {};
        setRiders((prev) =>
          prev.map((r) =>
            r._id === rider._id
              ? {
                  ...r,
                  threshold: d.threshold ?? r.threshold,
                  thresholdSource: d.thresholdSource ?? r.thresholdSource,
                  heldBalance: d.heldBalance ?? r.heldBalance,
                  isFlagged: typeof d.isFlagged === "boolean" ? d.isFlagged : r.isFlagged,
                  utilisation:
                    Number(d.threshold ?? r.threshold) > 0
                      ? Number(d.heldBalance ?? r.heldBalance) / Number(d.threshold ?? r.threshold)
                      : r.utilisation,
                }
              : r
          )
        );
        toast.success(res.data.message || "Limit updated.");
        if (d.released) {
          toast.success(`${rider.name || "This rider"} can receive orders again.`);
        }
        setEditor(null);
      } else {
        toast.error("The limit was not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The limit was not saved."));
    } finally {
      setSavingRow(false);
    }
  };

  const saveRiderVehicle = async (rider) => {
    if (!vehicleDraft.carrierType) {
      toast.error("Pick a vehicle type.");
      return;
    }
    try {
      setSavingRow(true);
      const body = {
        carrierType: vehicleDraft.carrierType,
        plateNumber: vehicleDraft.plateNumber || undefined,
        model: vehicleDraft.model || undefined,
        color: vehicleDraft.color || undefined,
        capacity: vehicleDraft.capacity === "" ? undefined : Number(vehicleDraft.capacity),
      };
      const res = await apiUpdateRiderVehicle(partnerId, rider._id, body);
      if (res?.data?.success) {
        const d = res.data.data || {};
        setRiders((prev) =>
          prev.map((r) =>
            r._id === rider._id
              ? {
                  ...r,
                  carrierType: d.carrierType ?? r.carrierType,
                  plateNumber: d.vehicleDetails?.plateNumber ?? r.plateNumber,
                }
              : r
          )
        );
        toast.success(res.data.message || "Vehicle updated.");
        setEditor(null);
      } else {
        toast.error("The vehicle was not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The vehicle was not saved."));
    } finally {
      setSavingRow(false);
    }
  };

  const th = {
    ...text.label,
    display: "table-cell",
    marginBottom: 0,
    padding: "10px 14px",
    textAlign: "left",
    borderBottom: `1px solid ${colors.line}`,
    whiteSpace: "nowrap",
  };
  const td = {
    ...text.body,
    padding: "12px 14px",
    borderBottom: `1px solid ${colors.lineSoft}`,
    verticalAlign: "top",
  };

  if (loading) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, textAlign: "center", color: colors.muted }}>
          <p style={{ ...text.body, margin: 0 }}>Loading the cash board...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, borderColor: colors.red }}>
          <h4 style={{ ...text.title, color: colors.red, marginBottom: 6 }}>
            Cash Control could not load
          </h4>
          <p style={{ ...text.body, margin: "0 0 16px" }}>{error}</p>
          <button type="button" style={button("secondary")} onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const blocked = Number(totals?.flaggedRiders || 0);
  const columnCount = canEditRiders ? 7 : 6;

  return (
    <div className="content">
      {/* Header */}
      <div
        style={{
          ...card,
          ...cardBody,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div style={{ minWidth: 240, flex: "1 1 320px" }}>
          <h4 style={text.title}>Cash Control</h4>
          <p style={text.subtitle}>
            Riders stop being offered orders once the Lusuku cash they are holding passes their
            limit, and start again once they deposit.
          </p>
        </div>
        <button type="button" style={button("secondary")} onClick={load}>
          Refresh
        </button>
      </div>

      {/* Totals */}
      <div
        style={{
          ...grid(4, 16),
          /* auto-fit rather than a fixed four so the row stacks instead of
             pushing the page sideways on a narrow window. */
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <StatCard
          label={
            <>
              Cash on the street
              <PartnerHelp topic="cashOnStreet" />
            </>
          }
          value={ugx(totals?.cashOnStreet)}
          sub="Collected and not yet deposited"
          dominant
        />
        <StatCard label="Riders holding it" value={Number(totals?.riders || 0)} />
        <StatCard
          label={
            <>
              Blocked riders
              <PartnerHelp topic="blockedRiders" />
            </>
          }
          value={blocked}
          sub={`${ugx(totals?.blockedCashOnStreet)} sitting behind the block`}
          tone={blocked > 0 ? colors.amber : colors.ink}
        />
        <StatCard
          label="Deposits in progress"
          value={Number(totals?.depositsPending || 0)}
          sub="Waiting to clear"
        />
      </div>

      {/* Fleet limit */}
      <div style={{ ...card, ...cardBody }}>
        <p style={text.section}>
          Fleet limit
          <PartnerHelp topic="fleetLimit" />
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-end" }}>
          <div style={{ minWidth: 200 }}>
            <span style={text.label}>Current default</span>
            <div
              style={{
                fontFamily: font.mono,
                fontVariantNumeric: "tabular-nums",
                fontSize: 22,
                fontWeight: 700,
                color: colors.ink,
              }}
            >
              {ugx(policy?.defaultRiderThreshold)}
            </div>
            <span style={{ ...pill(policy?.isPartnerSet ? "good" : "muted"), marginTop: 8 }}>
              {policy?.isPartnerSet ? "Your setting" : "Lusuku default"}
            </span>
            {!policy?.isPartnerSet && (
              <p style={text.help}>
                Platform default is {ugx(policy?.platformDefault)} until you set your own.
              </p>
            )}
          </div>

          {canSetFleetLimit ? (
            <div style={{ flex: "1 1 260px", minWidth: 220 }}>
              <label style={text.label} htmlFor="fleetLimit">
                New limit for every rider on the default
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <input
                  id="fleetLimit"
                  type="number"
                  min={min}
                  max={max}
                  step={1000}
                  value={fleetLimit}
                  onChange={(e) => setFleetLimit(e.target.value)}
                  {...inp({ maxWidth: 220 })}
                />
                <button
                  type="button"
                  style={button("primary", savingPolicy ? { opacity: 0.6, cursor: "wait" } : {})}
                  disabled={savingPolicy}
                  onClick={saveFleetLimit}
                >
                  {savingPolicy ? "Saving" : "Save limit"}
                </button>
              </div>
              <p style={text.help}>
                Anything from {ugx(min)} to {ugx(max)}. Riders whose limit was set individually keep
                theirs.
              </p>
            </div>
          ) : (
            <p style={{ ...text.help, maxWidth: 360 }}>
              Only the partner owner, or staff with full financial access, can change the fleet
              limit.
            </p>
          )}
        </div>
      </div>

      {/* Riders */}
      <div style={{ ...card, ...cardBody }}>
        <p style={text.section}>Riders, most cash first</p>

        {riders.length === 0 ? (
          <div style={{ padding: "28px 0", textAlign: "center" }}>
            <p style={{ ...text.body, margin: "0 0 6px", fontWeight: 600, color: colors.ink }}>
              No riders are linked to this partner yet.
            </p>
            <p style={{ ...text.body, margin: "0 0 16px", color: colors.muted }}>
              Add riders under My Riders and they will show up here as soon as they start carrying
              cash.
            </p>
            <button
              type="button"
              style={button("primary")}
              onClick={() => history.push("/admin/partner-riders")}
            >
              Go to My Riders
            </button>
          </div>
        ) : (
          /* Its own scroller: a seven column table on a laptop window would
             otherwise drag the whole page sideways. */
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 860, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Rider</th>
                  <th style={th}>
                    Vehicle
                    <PartnerHelp topic="vehicleClass" />
                  </th>
                  <th style={th}>Status</th>
                  <th style={th}>Cash held</th>
                  <th style={th}>
                    Limit
                    <PartnerHelp topic="cashLimit" />
                  </th>
                  <th style={th}>
                    Utilisation
                    <PartnerHelp topic="cashUtilisation" />
                  </th>
                  {canEditRiders && <th style={th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {riders.map((r) => {
                  const adminSet = r.thresholdSource === "ADMIN";
                  const open = editor?.riderId === r._id;
                  return (
                    <React.Fragment key={r._id}>
                      <tr>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: colors.ink }}>
                            {r.name || "Unnamed rider"}
                          </div>
                          <div style={{ fontFamily: font.mono, fontSize: 12, color: colors.muted }}>
                            {r.phoneNumber || "No phone"}
                          </div>
                        </td>

                        <td style={td}>
                          <div>{r.carrierType || "Not set"}</div>
                          <div style={{ ...text.help, margin: 0 }}>{r.plateNumber || "No plate"}</div>
                        </td>

                        <td style={td}>
                          <span style={pill(r.onlineStatus ? "good" : "muted")}>
                            {r.onlineStatus ? "Online" : "Offline"}
                          </span>
                          {!r.isActive && (
                            <div style={{ marginTop: 6 }}>
                              <span style={pill("muted")}>Inactive</span>
                            </div>
                          )}
                        </td>

                        <td style={td}>
                          <div style={text.numeric}>{ugx(r.heldBalance)}</div>
                          {r.pendingDepositId && (
                            <div style={{ marginTop: 6 }}>
                              <span style={pill("info")}>Deposit in progress</span>
                            </div>
                          )}
                        </td>

                        <td style={td}>
                          <div style={text.numeric}>{ugx(r.threshold)}</div>
                          {adminSet && <p style={text.help}>Set by Lusuku</p>}
                          {r.thresholdSource === "PARTNER" && <p style={text.help}>Set by you</p>}
                        </td>

                        <td style={td}>
                          <UtilisationBar value={r.utilisation} />
                          {r.isFlagged && (
                            <div style={{ marginTop: 6 }}>
                              <span style={pill("warn")}>Blocked</span>
                              {r.flaggedReason && (
                                <p style={text.help}>{r.flaggedReason}</p>
                              )}
                            </div>
                          )}
                        </td>

                        {canEditRiders && (
                          <td style={td}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              <button
                                type="button"
                                style={button(
                                  "secondary",
                                  adminSet ? { opacity: 0.5, cursor: "not-allowed" } : {}
                                )}
                                disabled={adminSet}
                                title={
                                  adminSet
                                    ? "Lusuku set this rider's limit, so it cannot be changed from the partner console."
                                    : "Change this rider's cash limit"
                                }
                                onClick={() => openLimitEditor(r)}
                              >
                                Set limit
                              </button>
                              <button
                                type="button"
                                style={button("quiet")}
                                onClick={() => openVehicleEditor(r)}
                              >
                                Vehicle
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>

                      {open && (
                        <tr>
                          <td
                            colSpan={columnCount}
                            style={{
                              ...td,
                              background: colors.ground,
                              borderBottom: `1px solid ${colors.line}`,
                            }}
                          >
                            {editor.mode === "limit" ? (
                              <div style={{ maxWidth: 520 }}>
                                <label style={text.label} htmlFor={`limit-${r._id}`}>
                                  Cash limit for {r.name || "this rider"}
                                </label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                  <input
                                    id={`limit-${r._id}`}
                                    type="number"
                                    min={min}
                                    max={max}
                                    step={1000}
                                    value={limitDraft}
                                    onChange={(e) => setLimitDraft(e.target.value)}
                                    {...inp({ maxWidth: 220 })}
                                  />
                                  <button
                                    type="button"
                                    style={button(
                                      "primary",
                                      savingRow ? { opacity: 0.6, cursor: "wait" } : {}
                                    )}
                                    disabled={savingRow}
                                    onClick={() => saveRiderLimit(r)}
                                  >
                                    {savingRow ? "Saving" : "Save"}
                                  </button>
                                  <button type="button" style={button("quiet")} onClick={closeEditor}>
                                    Cancel
                                  </button>
                                </div>
                                <p style={text.help}>
                                  Anything from {ugx(min)} to {ugx(max)}. Raising it above the cash
                                  this rider holds lets them take orders again.
                                </p>
                              </div>
                            ) : (
                              <div style={{ maxWidth: 720 }}>
                                <div
                                  style={{
                                    ...grid(3, 16),
                                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                                    gap: 16,
                                  }}
                                >
                                  <div>
                                    <label style={text.label} htmlFor={`vtype-${r._id}`}>
                                      Vehicle type
                                      <PartnerHelp topic="vehicleClass" />
                                    </label>
                                    <AppSelect
                                      id={`vtype-${r._id}`}
                                      value={vehicleDraft.carrierType}
                                      onChange={(e) =>
                                        setVehicleDraft((v) => ({ ...v, carrierType: e.target.value }))
                                      }
                                      {...inp()}
                                    >
                                      <option value="">Pick a type</option>
                                      {VEHICLE_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                          {t}
                                        </option>
                                      ))}
                                    </AppSelect>
                                  </div>
                                  <div>
                                    <label style={text.label} htmlFor={`plate-${r._id}`}>
                                      Plate number
                                    </label>
                                    <input
                                      id={`plate-${r._id}`}
                                      type="text"
                                      value={vehicleDraft.plateNumber}
                                      onChange={(e) =>
                                        setVehicleDraft((v) => ({ ...v, plateNumber: e.target.value }))
                                      }
                                      {...inp()}
                                    />
                                  </div>
                                  <div>
                                    <label style={text.label} htmlFor={`model-${r._id}`}>
                                      Model
                                    </label>
                                    <input
                                      id={`model-${r._id}`}
                                      type="text"
                                      value={vehicleDraft.model}
                                      onChange={(e) =>
                                        setVehicleDraft((v) => ({ ...v, model: e.target.value }))
                                      }
                                      {...inp()}
                                    />
                                  </div>
                                  <div>
                                    <label style={text.label} htmlFor={`colour-${r._id}`}>
                                      Colour
                                    </label>
                                    <input
                                      id={`colour-${r._id}`}
                                      type="text"
                                      value={vehicleDraft.color}
                                      onChange={(e) =>
                                        setVehicleDraft((v) => ({ ...v, color: e.target.value }))
                                      }
                                      {...inp()}
                                    />
                                  </div>
                                  <div>
                                    <label style={text.label} htmlFor={`cap-${r._id}`}>
                                      Capacity (kg)
                                    </label>
                                    <input
                                      id={`cap-${r._id}`}
                                      type="number"
                                      min={0}
                                      value={vehicleDraft.capacity}
                                      onChange={(e) =>
                                        setVehicleDraft((v) => ({ ...v, capacity: e.target.value }))
                                      }
                                      {...inp()}
                                    />
                                  </div>
                                </div>
                                <p style={text.help}>
                                  Vehicle class decides which orders this rider can be offered.
                                  Freight needs a van or larger.
                                </p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
                                  <button
                                    type="button"
                                    style={button(
                                      "primary",
                                      savingRow ? { opacity: 0.6, cursor: "wait" } : {}
                                    )}
                                    disabled={savingRow}
                                    onClick={() => saveRiderVehicle(r)}
                                  >
                                    {savingRow ? "Saving" : "Save vehicle"}
                                  </button>
                                  <button type="button" style={button("quiet")} onClick={closeEditor}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog {...confirmDialog.props} />
    </div>
  );
};

export default PartnerCashControl;
